import React, { useEffect, useState } from 'react';
import { Crown, Sparkles, Library as LibraryIcon, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

/**
 * Versioned "What's new" modal.
 *
 * - Shows once per version (localStorage key `sl_whats_new_seen`).
 * - Bump VERSION whenever you ship a new set of highlights.
 * - Mounted inside AppShell so it appears on any authenticated route.
 */
const VERSION = '2026-02-24';
const STORAGE_KEY = 'sl_whats_new_seen';

const HIGHLIGHTS = [
  {
    Icon: Crown,
    title: 'Top Tier is here',
    body:
      'Unlock 2× coin earn rate, offline library access, and early-access content for £36/yr (₦24k / $30 regional).',
    cta: { label: 'See plans', path: '/premium' },
    accent: 'from-amber-400 to-orange-500',
  },
  {
    Icon: Sparkles,
    title: 'Early-access content',
    body:
      'Writers can now flag uploads as "Early Access" — Top Tier readers get first look before everyone else.',
    accent: 'from-fuchsia-400 to-pink-500',
  },
  {
    Icon: LibraryIcon,
    title: 'Library duplication fixed',
    body:
      'Your uploaded series show exactly once now — no more ghost rows. No records were deleted.',
    accent: 'from-emerald-400 to-teal-500',
  },
];

const WhatsNewModal = ({ user }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Don't open on Premium / checkout / login routes — those pages
    // have their own CTAs that a full-screen modal would block, and
    // the sudden overlay was intercepting "Go Yearly" taps for users
    // who hadn't dismissed What's New (Feb 2026 fix).
    try {
      const path = (window.location.pathname || '').toLowerCase();
      if (
        path.startsWith('/premium') ||
        path.startsWith('/checkout') ||
        path.startsWith('/login') ||
        path.startsWith('/register') ||
        path.startsWith('/admin-login')
      ) {
        return;
      }
    } catch {
      /* window not available (SSR) */
    }
    try {
      if (localStorage.getItem(STORAGE_KEY) !== VERSION) {
        // Tiny delay so it doesn't fight with page paint
        const t = setTimeout(() => setOpen(true), 400);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage blocked — skip silently */
    }
  }, [user]);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, VERSION);
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
      data-testid="whats-new-modal"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          onClick={dismiss}
          className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors"
          data-testid="whats-new-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-indigo-600/30 via-purple-600/20 to-transparent border-b border-slate-800">
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-300 font-semibold">
            What's new
          </p>
          <h2 className="mt-1 text-2xl font-bold text-white">
            Fresh updates for you
          </h2>
          <p className="mt-1 text-sm text-slate-300">
            A quick look at what we've shipped since your last visit.
          </p>
        </div>

        <ul className="p-5 space-y-4">
          {HIGHLIGHTS.map(({ Icon, title, body, cta, accent }) => (
            <li key={title} className="flex gap-3" data-testid={`whats-new-item-${title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div
                className={`shrink-0 h-10 w-10 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center`}
              >
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">{title}</p>
                <p className="text-slate-300 text-sm leading-relaxed mt-0.5">
                  {body}
                </p>
                {cta && (
                  <button
                    onClick={() => {
                      dismiss();
                      navigate(cta.path);
                    }}
                    className="mt-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 transition-colors"
                    data-testid="whats-new-cta-premium"
                  >
                    {cta.label} →
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="px-5 pb-5">
          <Button
            onClick={dismiss}
            className="w-full bg-white text-slate-900 hover:bg-slate-100 font-semibold"
            data-testid="whats-new-dismiss"
          >
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;
